import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Receipt,
  Truck,
  PackageCheck,
  BarChart3,
  ShieldAlert,
  LifeBuoy,
  Folder,
  Settings as SettingsIcon,
  LogOut,
  ShieldCheck,
  RotateCcw,
  Boxes,
  X,
  Building2,
  SlidersHorizontal,
  Users as UsersIcon,
  Mail,
  TrendingUp,
  Coins,
  Plug,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const BOXHERO_LOGO_WHITE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_e93c448b-a938-4bcf-95ba-463bd62fed43/artifacts/2d6ua9tk_Logo_Wide_White%20%283%29.png";
const BOXHERO_TAGLINE_BADGE =
  "https://customer-assets.emergentagent.com/job_32254e95-6993-4c31-846d-f74b6176999f/artifacts/ag9vdnnt_Asset%204.png";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/orders-shipments", label: "Orders & Shipments", icon: Truck, testId: "nav-orders", module: "orders" },
  { to: "/delivery", label: "Delivery Tracking", icon: PackageCheck, testId: "nav-delivery", module: "delivery" },
  { to: "/billing", label: "Billing", icon: Receipt, testId: "nav-billing", module: "billing" },
  { to: "/invoices", label: "Invoices", icon: FileText, testId: "nav-invoices", module: "invoices" },
  { to: "/claims", label: "Claims", icon: ShieldAlert, testId: "nav-claims", module: "claims" },
  { to: "/returns", label: "Returns", icon: RotateCcw, testId: "nav-returns", module: "returns" },
  { to: "/inventory", label: "Inbound POs", icon: Boxes, testId: "nav-inventory", module: "inventory" },
  { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports", module: "reports" },
  { to: "/support", label: "Support", icon: LifeBuoy, testId: "nav-support" },
  { to: "/documents", label: "Documents", icon: Folder, testId: "nav-documents", module: "documents" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

function LogoWordmark() {
  return (
    <img src={BOXHERO_LOGO_WHITE} alt="BoxHero" className="h-11 w-auto" data-testid="sidebar-logo" />
  );
}

export default function Sidebar({ open = false, onClose = () => {}, collapsed = false }) {
  const { user, logout, can, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const inAdmin = location.pathname.startsWith("/admin");

  // Build stamp — when the live copy of the app was last deployed.
  const [deployedAt, setDeployedAt] = useState("");
  useEffect(() => {
    api.get("/version")
      .then(({ data }) => {
        const d = new Date(data.deployed_at);
        setDeployedAt(d.toLocaleString(undefined, {
          year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        }));
      })
      .catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      {/* Mobile overlay */}
      <div
        data-testid="sidebar-overlay"
        onClick={onClose}
        className={`fixed inset-0 bg-bh-navy-deep/50 z-40 lg:hidden transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      <aside
        data-testid="app-sidebar"
        className={`fixed inset-y-0 left-0 w-64 flex flex-col z-50 transform transition-transform duration-200 ${
          collapsed ? "lg:-translate-x-full" : "lg:translate-x-0"
        } ${open ? "translate-x-0" : "-translate-x-full"}`}
        style={{ backgroundColor: "#426db0" }}
      >
        {/* Top orange accent strip */}
        <div className="h-1.5 bg-bh-orange" />

        {/* Logo + mobile close */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-white/10">
          <LogoWordmark />
          <button
            data-testid="sidebar-close-btn"
            onClick={onClose}
            className="lg:hidden text-white/80 hover:text-white p-1"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4">
          {(user?.role === "returns_manager"
            ? NAV.filter((i) => ["/support", "/settings"].includes(i.to))
            : NAV.filter((i) => !i.module || can(i.module, "view"))
          ).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={onClose}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 mx-3 rounded-md transition-colors text-sm font-medium mt-1 ${
                    isActive
                      ? "bg-white/15 text-white border-l-2 border-bh-orange -ml-[1px]"
                      : "text-slate-100/80 hover:text-white hover:bg-white/10"
                  }`
                }
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
                <span className="flex-1">{item.label}</span>
                {item.soon && (
                  <span className="text-[10px] uppercase tracking-wider bg-white/10 text-slate-300 px-1.5 py-0.5 rounded">
                    Soon
                  </span>
                )}
              </NavLink>
            );
          })}

          {(user?.role === "admin" || user?.role === "returns_manager") && (
            <NavLink
              to="/admin"
              onClick={onClose}
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 mx-3 rounded-md transition-colors text-sm font-medium mt-3 ${
                  isActive
                    ? "bg-bh-orange/30 text-white border-l-2 border-bh-orange -ml-[1px]"
                    : "text-slate-100/80 hover:text-white hover:bg-white/10"
                }`
              }
            >
              <ShieldCheck className="w-[18px] h-[18px]" strokeWidth={2} />
              <span>{user?.role === "returns_manager" ? "Warehouse Console" : "Admin Console"}</span>
            </NavLink>
          )}

          {/* Console sub-navigation — only while inside the console */}
          {inAdmin && user?.role === "admin" && (
            <div className="mt-1 mb-2 ml-6 mr-3 pl-3 border-l border-white/15" data-testid="admin-subnav">
              <p className="px-3 py-2 text-[11px] leading-relaxed text-slate-100/50">
                Navigate the console from its own list — Company work on top, one customer&apos;s setup below.
              </p>
            </div>
          )}
        </nav>

        {/* Help card + logout */}
        <div className="p-4 border-t border-white/10 space-y-3">
          <div className="bg-white/5 rounded-lg p-4">
            <p className="text-white text-sm font-semibold">Need Help?</p>
            <p className="text-white/70 text-xs mt-1">Our team is here to help.</p>
            <button
              data-testid="contact-support-btn"
              onClick={() => { onClose(); navigate("/support"); }}
              className="mt-3 w-full border border-bh-orange text-bh-orange hover:bg-bh-orange hover:text-bh-navy-deep transition-colors text-xs font-semibold py-2 rounded-md"
            >
              Contact Support
            </button>
          </div>

          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-slate-100/80 hover:text-white hover:bg-white/10 rounded-md transition-colors text-sm font-medium"
          >
            <LogOut className="w-[18px] h-[18px]" />
            Log out
          </button>

          {/* Sub-brand badge */}
          <div className="pt-2 flex flex-col items-center gap-1.5">
            <img src={BOXHERO_TAGLINE_BADGE} alt="Your Brand's Fulfillment Partner · boxhero.com" className="w-40 h-auto opacity-95" />
            <p className="text-[10px] text-white/55 tabular-nums" data-testid="app-version">
              {deployedAt ? `Version ${deployedAt}` : "Version —"}
            </p>
          </div>
        </div>
      </aside>
    </>
  );
}
